import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form, Row, Col, Table, Dropdown } from 'react-bootstrap';
import baseURL from './baseUrl';
import logo from './logo-black.png';
import './Transactions.css';
import { Trash, PencilFill, PrinterFill, Whatsapp, ThreeDotsVertical } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { sendTransactionOnWhatsApp, fetchClientWhatsappNo, saveClientWhatsappNo } from './whatsappTransaction';

const Transactions = () => {
  // State Management
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Initialize with an empty array
  const [searchParams, setSearchParams] = useState({
    receiptNo: '',
    clientName: '',
    startDate: '',
    endDate: '',
    transactionType: ''
  });

  // Modal and Edit States
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editWhatsappNo, setEditWhatsappNo] = useState('');
  const [editWhatsappStatus, setEditWhatsappStatus] = useState('');
  
  // Loading and Error States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Transaction Types
  const TRANSACTION_TYPES = [
    'buy', 
    'sell', 
    'advance', 
    'cash', 
    'pure_weight'
  ];

  // Utility Functions
  const formatPureWeight = (weight) => {
    if (!weight) return '0.000';
    const [whole, decimal = ''] = String(weight).split('.');
    const formattedDecimal = (decimal + '000').slice(0, 3);
    return `${whole}.${formattedDecimal}`;
  };

  const calculatePureWeight = (weights, touches) => {
    if (!Array.isArray(weights) || !Array.isArray(touches)) return 0;
    
    return weights.reduce((total, weight, index) => {
      const touch = touches[index] || 0;
      return total + ((parseFloat(weight) || 0) * (parseFloat(touch) || 0)) / 100;
    }, 0).toFixed(3);
  };

  // Rate is stored as ₹ per 10g (same as Buy / calculateTransactionAmounts / print).
  const calculateAmount = (weight, touch, rate) => {
    weight = parseFloat(weight) || 0;
    touch = parseFloat(touch) || 0;
    rate = parseFloat(rate) || 0;
    const pureWeight = precisionTruncate(weight * (touch / 100), 3);
    return truncateToWholeNumber(pureWeight * (rate / 10));
  };

  const precisionTruncate = (value, precision = 3) => {
    if (value == null) return 0;
    const factor = Math.pow(10, precision);
    const truncatedValue = Math.floor(value * factor) / factor;
    return Number(truncatedValue.toFixed(precision));
  };

  const truncateToWholeNumber = (value) => {
    if (value == null) return 0;
    return Math.trunc(Number(value));
  };

  const roundCurrency = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  const isSpeedTransaction = (t) => t?.type === 'speed';

  const isBuySellTransaction = (t) => t?.type === 'buy' || t?.type === 'sell';

  const shouldUseReceiptEditView = (t) =>
    isBuySellTransaction(t) &&
    (t?.articleType === 'pureWeight' || t?.articleType === 'langdiWeight');

  const getReceiptRowPureWeight = (transaction, index) => {
    const weight = parseFloat(transaction.langdiWeight?.[index]) || 0;
    if (transaction.articleType === 'pureWeight') {
      return precisionTruncate(weight, 3);
    }
    const touch = parseFloat(transaction.touch?.[index]) || 0;
    return precisionTruncate(weight * (touch / 100), 3);
  };

  const getReceiptRowAmount = (transaction, index) => {
    const stored = transaction.totalAmounts?.[index];
    if (stored != null && stored !== '') {
      return truncateToWholeNumber(parseFloat(stored) || 0);
    }
    if (transaction.articleType === 'pureWeight') {
      const pureWeight = getReceiptRowPureWeight(transaction, index);
      const rate = parseFloat(transaction.rate?.[index]) || 0;
      return truncateToWholeNumber(pureWeight * (rate / 10));
    }
    return calculateAmount(
      transaction.langdiWeight[index],
      transaction.touch[index],
      transaction.rate[index]
    );
  };

  const handleAmountGivenChange = (value) => {
    const amountGiven = parseFloat(value) || 0;
    const totalAmount = truncateToWholeNumber(
      selectedTransaction.total || selectedTransaction.totalAmount || 0
    );
    setSelectedTransaction({
      ...selectedTransaction,
      amountGiven,
      pendingAmount: truncateToWholeNumber(totalAmount - amountGiven)
    });
  };

  const calculateTransactionAmounts = (transaction) => {
    let totalAmount = 0;
    const totalAmounts = []; // Add this to store individual row amounts
    
    if (transaction.articleType === 'langdiWeight') {
      if (isSpeedTransaction(transaction)) {
        // Single benchmark rate (₹/10g) for all lines — matches backend calculateAndCloseTransaction (rate[0]).
        const baseRate = parseFloat(transaction.rate?.[0]) || 0;
        const n = transaction.langdiWeight?.length || 0;
        for (let index = 0; index < n; index++) {
          const weight = parseFloat(transaction.langdiWeight[index] || 0);
          const touch = parseFloat(transaction.touch[index] || 0);
          const pureWeight = precisionTruncate(weight * (touch / 100), 3);
          const currentAmount = truncateToWholeNumber(pureWeight * (baseRate / 10));
          totalAmounts.push(currentAmount);
          totalAmount += currentAmount;
        }
      } else {
        // Keep same formula as Buy: truncate pure weight (3) then trunc row total.
        transaction.rate.forEach((rate, index) => {
          const weight = parseFloat(transaction.langdiWeight[index] || 0);
          const touch = parseFloat(transaction.touch[index] || 0);
          const pureWeight = precisionTruncate(weight * (touch / 100), 3);
          const currentAmount = truncateToWholeNumber(pureWeight * (parseFloat(rate) / 10));
          totalAmounts.push(currentAmount);
          totalAmount += currentAmount;
        });
      }
    } else if (transaction.articleType === 'pureWeight') {
      // Keep same formula as Buy: truncate pure weight (3) then trunc row total.
      transaction.rate.forEach((rate, index) => {
        const pureWeight = precisionTruncate(parseFloat(transaction.amount[index] || 0), 3);
        const currentAmount = truncateToWholeNumber(pureWeight * (parseFloat(rate) / 10));
        totalAmounts.push(currentAmount);
        totalAmount += currentAmount;
      });
    }

    // Calculate pending amount
    const amountGiven = parseFloat(transaction.amountGiven || 0);
    const roundedTotal = truncateToWholeNumber(totalAmount);
    const pendingAmount = isSpeedTransaction(transaction)
      ? roundedTotal
      : truncateToWholeNumber(roundedTotal - amountGiven);

    return {
      totalAmount: roundedTotal,
      pendingAmount,
      totalAmounts: totalAmounts // Return the array of individual amounts
    };
  };

  /** Speed: sync rate[0] to all rows, pure total, balance × rate/10 settlement (backend-aligned). */
  const recalculateSpeedFinancials = (transaction) => {
    if (!isSpeedTransaction(transaction) || transaction.articleType !== 'langdiWeight') {
      return transaction;
    }
    const len = transaction.langdiWeight?.length || 0;
    const base = parseFloat(transaction.rate?.[0]) || 0;
    const syncedRates = Array.from({ length: len }, () => base);
    const next = { ...transaction, rate: syncedRates };
    const { totalAmount, pendingAmount, totalAmounts } = calculateTransactionAmounts(next);
    const totalPure = parseFloat(calculatePureWeight(next.langdiWeight, next.touch)) || 0;
    const pureGiven =
      parseFloat(next.pureWeightGiven ?? next.pureWeightGivenInGrams ?? 0) || 0;
    const balance = Number((totalPure - pureGiven).toFixed(3));
    const amountToBePaid = balance > 0 ? Math.floor((balance * base) / 10) : 0;
    const amountToBeAdded = balance < 0 ? Math.floor((Math.abs(balance) * base) / 10) : 0;
    return {
      ...next,
      totalAmount,
      pendingAmount,
      totalAmounts,
      total: totalAmount,
      pureWeightCalculated: Number(totalPure.toFixed(3)),
      amountToBePaid,
      amountToBeAdded,
      balanceWeight: balance,
      balancePureWeight: balance
    };
  };

  const parseEditableNumber = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const p = parseFloat(value);
    return Number.isNaN(p) ? '' : p;
  };

  const displayNumberOrEmpty = (v) =>
    v === '' || v == null || (typeof v === 'number' && Number.isNaN(v)) ? '' : v;

  const updateTransactionRow = (transaction, index, field, value) => {
    const updatedTransaction = { ...transaction };
    
    // Update the specific field
    switch (field) {
      case 'weight': {
        const nextVal = parseEditableNumber(value);
        updatedTransaction.langdiWeight = [...(updatedTransaction.langdiWeight || [])];
        updatedTransaction.langdiWeight[index] = nextVal;
        // Stock/API use `amount` for both langdi and pure weight lines.
        if (
          updatedTransaction.articleType === 'langdiWeight' ||
          updatedTransaction.articleType === 'pureWeight'
        ) {
          updatedTransaction.amount = [...(updatedTransaction.amount || [])];
          updatedTransaction.amount[index] = nextVal;
        }
        break;
      }
      case 'touch':
        updatedTransaction.touch = [...(updatedTransaction.touch || [])];
        updatedTransaction.touch[index] = parseEditableNumber(value);
        break;
      case 'rate':
        if (isSpeedTransaction(updatedTransaction)) {
          const nextRate = parseEditableNumber(value);
          const v =
            typeof nextRate === 'number' && !Number.isNaN(nextRate) ? nextRate : 0;
          const rowCount = updatedTransaction.langdiWeight?.length || 1;
          updatedTransaction.rate = Array(rowCount).fill(v);
        } else {
          updatedTransaction.rate = [...(updatedTransaction.rate || [])];
          updatedTransaction.rate[index] = parseEditableNumber(value);
        }
        break;
      case 'amount':
        updatedTransaction.amount = [...(updatedTransaction.amount || [])];
        updatedTransaction.amount[index] = parseFloat(value);
        break;
    }

    if (isSpeedTransaction(updatedTransaction)) {
      return recalculateSpeedFinancials(updatedTransaction);
    }

    // Recalculate amounts
    const { totalAmount, pendingAmount, totalAmounts } = calculateTransactionAmounts(updatedTransaction);
    updatedTransaction.totalAmount = totalAmount;
    updatedTransaction.pendingAmount = pendingAmount;
    updatedTransaction.totalAmounts = totalAmounts;
    updatedTransaction.total = totalAmount;

    if (updatedTransaction.articleType === 'pureWeight') {
      const sumPw = (updatedTransaction.amount || []).reduce((acc, w) => {
        const n = w === '' || w == null ? 0 : parseFloat(w);
        return acc + (Number.isNaN(n) ? 0 : n);
      }, 0);
      updatedTransaction.pureWeightCalculated = Number(sumPw.toFixed(3));
    }

    return updatedTransaction;
  };

  // Fetch and Filter Functions
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching Transactions - Search Parameters:', {
        searchParams: searchParams,
        timestamp: new Date().toISOString()
      });

      const response = await axios.get(`${baseURL}/api/transactions`, {
        params: searchParams
      });
      
      console.log('📦 Transactions Fetch Response:', {
        status: response.status,
        totalTransactions: response.data.length,
        firstTransaction: response.data[0],
        lastTransaction: response.data[response.data.length - 1]
      });

      setTransactions(response.data);
      
      // Initial filtering
      const filtered = filterTransactions(response.data, searchParams);
      
      console.log('🔬 Filtered Transactions:', {
        totalFiltered: filtered.length,
        filterParams: searchParams,
        filterDetails: filtered.map(t => ({
          receiptNo: t.receiptNo,
          type: t.type,
          clientName: t.clientName
        }))
      });
      
      setFilteredTransactions(filtered);
    } catch (error) {
      console.error('❌ Error in Fetching Transactions:', {
        message: error.message,
        errorResponse: error.response?.data,
        status: error.response?.status,
        url: `${baseURL}/api/transactions`,
        searchParams: searchParams
      });
      
      setError(`Error in fetching transactions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = (data, params) => {
    if (!data || !Array.isArray(data)) return [];

    let filtered = data;

    // Filter by Receipt No
    if (params.receiptNo) {
      filtered = filtered.filter(t => 
        t.receiptNo.toLowerCase().includes(params.receiptNo.toLowerCase())
      );
    }

    // Filter by Client Name
    if (params.clientName) {
      filtered = filtered.filter(t => 
        t.clientName.toLowerCase().includes(params.clientName.toLowerCase())
      );
    }

    // Filter by Transaction Type
    if (params.transactionType) {
      filtered = filtered.filter(t => 
        t.type === params.transactionType || 
        t.advanceType === params.transactionType
      );
    }

    // Filter by Date Range
    if (params.startDate && params.endDate) {
      const start = new Date(params.startDate);
      const end = new Date(params.endDate);
      
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    return filtered;
  };

  // Transaction Management Functions
  const handleEditTransaction = async (transaction) => {
    let normalizedTransaction = normalizeTransactionForEdit(transaction);
    if (isSpeedTransaction(normalizedTransaction)) {
      normalizedTransaction = recalculateSpeedFinancials(normalizedTransaction);
    }
    setSelectedTransaction(normalizedTransaction);
    setEditWhatsappNo('');
    setEditWhatsappStatus('');
    setShowEditModal(true);
    if (normalizedTransaction.clientName) {
      const phone = await fetchClientWhatsappNo(normalizedTransaction.clientName);
      setEditWhatsappNo(phone || '');
    }
  };

  const handleEditWhatsappBlur = async () => {
    const clientName = selectedTransaction?.clientName;
    if (!clientName) return;
    setEditWhatsappStatus('saving');
    const saved = await saveClientWhatsappNo(clientName, editWhatsappNo);
    setEditWhatsappStatus(saved ? 'saved' : 'error');
  };

  const getTransactionDetailCell = (transaction) => {
    if (transaction.articleType === 'langdiWeight') {
      const wt = transaction.amount?.[0] ?? transaction.langdiWeight?.[0];
      return wt != null ? `${Number(wt).toLocaleString()} (L)` : '-';
    }
    if (transaction.articleType === 'pureWeight') {
      if (
        transaction.transactionType === 'advance' &&
        ['given', 'taken'].includes(transaction.advanceType)
      ) {
        return (transaction.total || 0).toLocaleString();
      }
      return transaction.amount?.[0] != null
        ? Number(transaction.amount[0]).toLocaleString()
        : '-';
    }
    if (transaction.articleType === 'cashAmount') {
      return transaction.amount?.[0] != null
        ? `₹${Number(transaction.amount[0]).toLocaleString()}`
        : '-';
    }
    if (transaction.articleType === 'article') {
      return transaction.amount?.[0] != null
        ? `${transaction.amount[0]} PCS`
        : '-';
    }
    return formatPureWeight(transaction.pureWeightCalculated);
  };

  const normalizeTransactionForEdit = (transaction) => {
    // Table rows iterate `langdiWeight`; for pure (and legacy) rows weights live in `amount`.
    const langdiSource =
      Array.isArray(transaction.langdiWeight) && transaction.langdiWeight.length > 0
        ? transaction.langdiWeight
        : (transaction.amount || []);

    const maxLength = Math.max(
      langdiSource.length || 1,
      transaction.touch?.length || 1,
      transaction.rate?.length || 1,
      transaction.amount?.length || 1
    );

    const normalizeArray = (arr, defaultValue = 0) => {
      if (!Array.isArray(arr)) arr = [arr];
      while (arr.length < maxLength) {
        arr.push(defaultValue);
      }
      return arr;
    };

    const baseRateFirst = parseFloat(
      Array.isArray(transaction.rate) ? transaction.rate[0] : transaction.rate
    );
    const rateOut = isSpeedTransaction(transaction)
      ? Array.from(
          { length: maxLength },
          () => (Number.isFinite(baseRateFirst) ? baseRateFirst : 0)
        )
      : normalizeArray(transaction.rate);

    return {
      ...transaction,
      langdiWeight: normalizeArray(langdiSource),
      touch: normalizeArray(transaction.touch, 0),
      rate: rateOut,
      amount: normalizeArray(transaction.amount),
      totalAmounts: normalizeArray(transaction.totalAmounts),
      pureWeightGiven: transaction.pureWeightGiven ?? transaction.pureWeightGivenInGrams ?? 0
    };
  };

  const saveTransactionEdit = async () => {
    try {
      let transactionToSave = { ...selectedTransaction };

      if (
        (transactionToSave.articleType === 'langdiWeight' ||
          transactionToSave.articleType === 'pureWeight') &&
        Array.isArray(transactionToSave.langdiWeight)
      ) {
        transactionToSave.amount = transactionToSave.langdiWeight.map((w) => parseFloat(w) || 0);
      }

      if (transactionToSave.articleType === 'pureWeight' && Array.isArray(transactionToSave.amount)) {
        const sumPw = transactionToSave.amount.reduce((a, w) => a + (parseFloat(w) || 0), 0);
        transactionToSave.pureWeightCalculated = Number(sumPw.toFixed(3));
      }

      if (isSpeedTransaction(transactionToSave)) {
        transactionToSave = recalculateSpeedFinancials(transactionToSave);
      } else {
        const { totalAmount, pendingAmount, totalAmounts } = calculateTransactionAmounts(transactionToSave);
        transactionToSave.totalAmount = totalAmount;
        transactionToSave.pendingAmount = pendingAmount;
        transactionToSave.totalAmounts = totalAmounts;
        transactionToSave.total = totalAmount;
      }

      const coerceArr = (arr) =>
        Array.isArray(arr) ? arr.map((v) => (v === '' || v == null ? 0 : Number(v)) || 0) : arr;
      transactionToSave.langdiWeight = coerceArr(transactionToSave.langdiWeight);
      transactionToSave.touch = coerceArr(transactionToSave.touch);
      transactionToSave.rate = coerceArr(transactionToSave.rate);
      transactionToSave.amount = coerceArr(transactionToSave.amount);
      transactionToSave.totalAmounts = coerceArr(transactionToSave.totalAmounts);

      if (
        transactionToSave.pureWeight != null &&
        typeof transactionToSave.pureWeight === 'object' &&
        transactionToSave.pureWeight.$numberDecimal != null
      ) {
        transactionToSave.pureWeight = parseFloat(transactionToSave.pureWeight.$numberDecimal) || 0;
      }

      if (isSpeedTransaction(transactionToSave)) {
        const pg = Number(transactionToSave.pureWeightGiven) || 0;
        transactionToSave.pureWeightGiven = pg;
        transactionToSave.pureWeightGivenInGrams = pg;
      }

      delete transactionToSave.__v;

      const response = await axios.put(`${baseURL}/api/transactions/update`, transactionToSave);

      const saved = response.data.transaction || response.data;
      const savedId = saved?._id != null ? String(saved._id) : '';
      const updatedTransactions = transactions.map((t) =>
        String(t._id) === savedId ? saved : t
      );

      setTransactions(updatedTransactions);
      setShowEditModal(false);
      setSelectedTransaction(null);
      setError(null);
    } catch (error) {
      const detail =
        error.response?.data?.error ||
        error.response?.data?.details ||
        error.response?.data?.message ||
        error.message;
      setError(`Error in updating transaction: ${detail}`);
      console.error('saveTransactionEdit', error.response?.data || error);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this transaction?'
      );
      
      if (!confirmDelete) return;

      await axios.delete(`${baseURL}/api/transactions/transactions/${transactionId}`);

      setTransactions(prevTransactions => 
        prevTransactions.filter(t => t._id !== transactionId)
      );

      setFilteredTransactions(prevTransactions => 
        prevTransactions.filter(t => t._id !== transactionId)
      );
    } catch (error) {
      setError(`Error in deleting transaction: ${error.message}`);
    }
  };

  const handleWhatsAppTransaction = async (transaction) => {
    if (!transaction) return;
    await sendTransactionOnWhatsApp(transaction);
  };

  const handlePrintTransaction = (transaction) => {
    if (!transaction) {
      console.error('No transaction data available for printing.');
      return;
    }

    const { date, receiptNo, clientName } = transaction;
    if (!date || !receiptNo || !clientName) {
      console.error('Transaction is missing required properties.');
      return;
    }

    // Calculate number of items to determine scaling
    let itemCount = 0;
    if (transaction.articleType === 'langdiWeight' && transaction.amountWithPureWeight) {
      itemCount = transaction.amountWithPureWeight.length;
    } else if (transaction.articleType === 'langdiWeight' && transaction.langdiWeight) {
      itemCount = Array.isArray(transaction.langdiWeight) ? transaction.langdiWeight.length : 0;
    } else if (transaction.articleType === 'pureWeight' && transaction.amount) {
      itemCount = Array.isArray(transaction.amount) ? transaction.amount.length : 0;
    } else if (transaction.articleType === 'cashAmount' && transaction.amount) {
      itemCount = Array.isArray(transaction.amount) ? transaction.amount.length : 0;
    }

    // Calculate scale factor based on item count
    let scaleFactor = 1;
    if (itemCount <= 1) {
      scaleFactor = 1.0;
    } else if (itemCount === 2) {
      scaleFactor = 0.98;
    } else if (itemCount === 3) {
      scaleFactor = 0.92;
    } else if (itemCount === 4) {
      scaleFactor = 0.88;
    } else if (itemCount >= 5) {
      scaleFactor = 0.85;
    }

    // Calculate scaled values
    const baseFontSize = 14;
    const scaledFontSize = Math.max(13, baseFontSize * scaleFactor);
    const scaledHeaderFont = Math.max(11, 12 * scaleFactor);
    const scaledMetaFont = Math.max(12, 13 * scaleFactor);
    const scaledReceiptNoFont = Math.max(17, 19 * scaleFactor);
    const scaledNameFont = Math.max(19, 21 * scaleFactor);
    const scaledInfoFont = Math.max(12, 14 * scaleFactor);
    const scaledFooterFont = Math.max(10, 11 * scaleFactor);
    const scaledDateFont = Math.max(12, 13 * scaleFactor);
    
    const scaledPadding = Math.max(6, 8 * scaleFactor);
    const scaledContainerPadding = Math.max(15, 20 * scaleFactor);
    const scaledMargin = Math.max(6, 8 * scaleFactor);
    const scaledLogoWidth = Math.max(100, 120 * scaleFactor);
    const scaledGap = Math.max(8, 12 * scaleFactor);
    const scaledRowGap = Math.max(1.5, 2 * scaleFactor);
    const scaledColumnGap = Math.max(5, 6 * scaleFactor);

    const printWindow = document.createElement('iframe');
    printWindow.style.position = 'absolute';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = 'none';
    document.body.appendChild(printWindow);

    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Print Transaction</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; margin: 0; padding: ' + scaledContainerPadding + 'px; background-color: #f9f9f9; }');
    doc.write('@page { margin: 0; size: A4; }');
    doc.write('.container { width: 80%; max-width: 100%; margin: auto; padding: ' + scaledContainerPadding + 'px; background: #fff; border: 1px solid #ccc; border-radius: 8px; page-break-inside: avoid; }');
    doc.write('h1 { text-align: center; font-size: ' + scaledHeaderFont + 'px; margin-bottom: 0px; margin-top: -10px; }');
    doc.write('.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: ' + (scaledMargin * 1.5) + 'px; }');
    doc.write('.logo { width: ' + scaledLogoWidth + 'px; height: auto; object-fit: contain; filter:contrast(220%); }');
    doc.write('.table { width: 100%; border-collapse: collapse; margin-top: ' + scaledMargin + 'px; font-size: ' + scaledFontSize + 'px; page-break-inside: avoid; }');
    doc.write('.table th, .table td { border: 1px solid #000; padding: ' + scaledPadding + 'px; text-align: center; }');
    doc.write('.table th { background-color: #f2f2f2; font-weight: bold; }');
    doc.write('.table tbody tr { page-break-inside: avoid; }');
    doc.write('.totals { margin-top: ' + scaledMargin + 'px; width: 100%; border-collapse: collapse; page-break-inside: avoid; }');
    doc.write('.total-row { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; font-size: ' + scaledFontSize + 'px; page-break-inside: avoid; }');
    doc.write('.total-row > span { padding: ' + scaledPadding + 'px; border-right: 1px solid #000; }');
    doc.write('.total-row > span:first-child { font-weight: bold; text-align: right; background: #f2f2f2; }');
    doc.write('.total-row > span:last-child { text-align: right; border-right: none; }');
    doc.write('.total-row:first-child { border-bottom: none; }');
    doc.write('.meta-row { display: grid; grid-template-columns: 1fr 1fr; gap: ' + scaledGap + 'px; margin: ' + (scaledMargin * 0.5) + 'px 0; font-size: ' + scaledMetaFont + 'px; }');
    doc.write('.meta-item { }');
    doc.write('.date-block { font-size: ' + scaledDateFont + 'px; line-height: 1.2; display: grid; grid-template-columns: auto auto; column-gap: ' + scaledColumnGap + 'px; row-gap: ' + scaledRowGap + 'px; justify-content: end; text-align: left; }');
    doc.write('.date-label { font-weight: 600; }');
    doc.write('.info-lines { font-size: ' + scaledInfoFont + 'px; margin: ' + (scaledMargin * 0.5) + 'px 0 ' + (scaledMargin * 0.6) + 'px 0; font-weight: 600; page-break-inside: avoid; }');
    doc.write('.info-row { display: grid; grid-template-columns: 1fr 1fr; column-gap: ' + scaledGap + 'px; }');
    doc.write('.info-item { text-align: left; }');
    doc.write('.footer-line { margin-top: ' + (scaledMargin * 0.5) + 'px; text-align: left; font-size: ' + scaledFooterFont + 'px; color: #333; }');
    doc.write('</style>');
    doc.write('</head><body>');
    
    doc.write('<div class="container">');
    doc.write('<h1>|| SHREE GANESHAY NAMH ||</h1>');
    doc.write('<div class="header">');
    doc.write('<img src="' + logo + '" class="logo" alt="Logo">');
    doc.write('<div class="date-block">');
    doc.write('<div class="date-label">Date:</div><div>' + new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + '</div>');
    doc.write('<div class="date-label">Time:</div><div>' + new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) + '</div>');
    doc.write('<div class="date-label">Name:</div><div>Manoj Shinde</div>');
    doc.write('<div class="date-label">Mobile:</div><div>8551881892</div>');
    doc.write('</div>');
    doc.write('</div>');
    
    doc.write('<div class="meta-row">');
    doc.write('<div class="meta-item"><strong>Receipt No:</strong> ' + '<span style="font-weight: bold; font-size: ' + scaledReceiptNoFont + 'px; color: #000;">' + (receiptNo || 'N/A') + '</span><br><strong>Name:</strong> <span style="font-weight: bold; font-size: ' + scaledNameFont + 'px; color: #000;">' + (clientName || 'N/A') + '</span></div>');
    doc.write('</div>');
    
    // Table for Transaction Details
    doc.write('<table class="table">');
    doc.write('<thead>');
    
    // Dynamic header based on article type
    if (transaction.articleType === 'pureWeight') {
      doc.write('<tr><th>Weight</th><th>Pure Weight</th><th>Rate</th><th>Amount</th></tr>');
    } else if (transaction.articleType === 'langdiWeight') {
      doc.write('<tr><th>Weight</th><th>Touch</th><th>Pure Weight</th><th>Rate</th><th>Amount</th></tr>');
    } else if (transaction.articleType === 'cashAmount') {
      doc.write('<tr><th>Amount</th><th>Total</th></tr>');
    } else {
      // Default for legacy transactions
      doc.write('<tr><th>Weight</th><th>Touch</th><th>Pure Weight</th><th>Rate</th><th>Amount</th></tr>');
    }
    
    doc.write('</thead>');
    doc.write('<tbody>');
    
    // Render Rows based on article type
    if (transaction.articleType === 'pureWeight') {
      const unit = transaction.unit || 'g';
      (transaction.amount || []).forEach((amount, index) => {
        const weight = parseFloat(amount) || 0;
        const rate = parseFloat(transaction.rate?.[index] || 0);
        const ratePerGram = rate / 10;
        const pureWeight = weight;
        const total = Math.trunc(pureWeight * ratePerGram);
        
        doc.write('<tr>');
        doc.write('<td>' + weight.toFixed(3) + ' ' + unit + '</td>');
        doc.write('<td>' + pureWeight.toFixed(3) + ' ' + unit + '</td>');
        doc.write('<td>₹' + rate.toFixed(2) + '</td>');
        doc.write('<td>₹' + total + '</td>');
        doc.write('</tr>');
      });
    } else if (transaction.articleType === 'langdiWeight') {
      const unit = transaction.unit || 'g';
      (transaction.langdiWeight || []).forEach((weight, index) => {
        const touch = parseFloat(transaction.touch?.[index] || 0);
        const rate = parseFloat(transaction.rate?.[index] || 0);
        const pureWeight = Math.floor((weight * touch / 100) * 1000) / 1000;
        const ratePerGram = rate / 10;
        const total = Math.trunc(pureWeight * ratePerGram);
        
        doc.write('<tr>');
        doc.write('<td>' + parseFloat(weight).toFixed(3) + ' ' + unit + '</td>');
        doc.write('<td>' + touch.toFixed(2) + '%</td>');
        doc.write('<td>' + pureWeight.toFixed(3) + ' ' + unit + '</td>');
        doc.write('<td>₹' + rate.toFixed(2) + '/10g</td>');
        doc.write('<td>₹' + total + '</td>');
        doc.write('</tr>');
      });
    } else if (transaction.articleType === 'cashAmount') {
      (transaction.amount || []).forEach((amount, index) => {
        const total = Math.trunc(parseFloat(transaction.totalAmounts?.[index] || amount || 0));
        doc.write('<tr>');
        doc.write('<td>₹' + parseFloat(amount).toFixed(2) + '</td>');
        doc.write('<td>₹' + total + '</td>');
        doc.write('</tr>');
      });
    } else {
      // Legacy format fallback
      (transaction.langdiWeight || []).forEach((weight, index) => {
        const touch = parseFloat(transaction.touch?.[index] || 0);
        const rate = parseFloat(transaction.rate?.[index] || 0);
        const pureWeight = Math.floor((weight * touch / 100) * 1000) / 1000;
        const ratePerGram = rate / 10;
        const total = Math.trunc(pureWeight * ratePerGram);
        
        doc.write('<tr>');
        doc.write('<td>' + parseFloat(weight).toFixed(3) + '</td>');
        doc.write('<td>' + touch.toFixed(2) + '%</td>');
        doc.write('<td>' + pureWeight.toFixed(3) + '</td>');
        doc.write('<td>₹' + rate.toFixed(2) + '</td>');
        doc.write('<td>₹' + total + '</td>');
        doc.write('</tr>');
      });
    }
    
    doc.write('</tbody>');
    doc.write('</table>');
    
    // Total Section
    doc.write('<div class="totals">');
    doc.write('<div class="total-row">');
    doc.write('<span>Total Amount:</span>');
    doc.write('<span>₹' + Math.trunc(parseFloat(transaction.total || transaction.totalAmount || 0)) + '</span>');
    doc.write('</div>');
    
    if (transaction.amountGiven && transaction.amountGiven > 0) {
      doc.write('<div class="total-row">');
      doc.write('<span>Amount ' + (transaction.type === 'buy' ? 'Given' : 'Received') + ':</span>');
      doc.write('<span>₹' + Math.trunc(parseFloat(transaction.amountGiven)) + '</span>');
      doc.write('</div>');
      
      doc.write('<div class="total-row">');
      doc.write('<span>Pending Amount:</span>');
      doc.write('<span>₹' + Math.trunc(parseFloat(transaction.pendingAmount || 0)) + '</span>');
      doc.write('</div>');
    }
    
    if (transaction.advance?.cashAmount > 0) {
      doc.write('<div class="total-row">');
      doc.write('<span>Advance Applied:</span>');
      doc.write('<span>₹' + Math.trunc(parseFloat(transaction.advance.cashAmount)) + '</span>');
      doc.write('</div>');
    }
    
    doc.write('</div>');
    
    // Bottom info lines
    doc.write('<div class="info-lines">');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* हमारे यहाँ लंगड़ी एक्सचेंज करके मिलता है</div>');
    doc.write('<div class="info-item">* RSBL गोल्ड कॉइन मिलता है</div>');
    doc.write('</div>');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* AQUA REFINE 995 और 999 मिलता है</div>');
    doc.write('<div class="info-item">* गोल्ड और सिल्वर मेल्ट किया जाता है</div>');
    doc.write('</div>');
    doc.write('<div class="footer-line">powered by Riddhi Infotech - 7499391121</div>');
    doc.write('</div>');
    
    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();

    // Wait for the iframe content to load before printing
    printWindow.contentWindow.onload = function() {
      printWindow.contentWindow.focus();
      printWindow.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(printWindow);
      }, 100);
    };
  };

  const handleBuyReceipt = async (transaction) => {
    try {
      // Navigate to Buy component with transaction details
      navigate('/buy', { 
        state: { 
          transaction: transaction,
          fromTransactions: true 
        } 
      });
    } catch (error) {
      console.error('Error handling Buy receipt:', error);
      setErrorMessage('Failed to load Buy receipt');
    }
  };

  const handleSpeedReceipt = async (transaction) => {
    try {
      // Navigate to Speed component with transaction details
      navigate('/speed', { 
        state: { 
          transaction: transaction,
          fromTransactions: true 
        } 
      });
    } catch (error) {
      console.error('Error handling Speed receipt:', error);
      setErrorMessage('Failed to load Speed receipt');
    }
  };

  // Modal and UI Helpers
  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedTransaction(null);
    setEditWhatsappNo('');
    setEditWhatsappStatus('');
  };

  const handleError = (error, context) => {
    const errorMessage = error.response?.data?.message || error.message;
    setError(`Error in ${context}: ${errorMessage}`);
  };

  // Effect Hooks
  const navigate = useNavigate();
  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    const filtered = filterTransactions(transactions, searchParams);
    setFilteredTransactions(filtered);
  }, [searchParams, transactions]);

  // Render Methods
  const formatTxnTotal = (transaction) => {
    if (
      transaction.transactionType === 'advance' ||
      transaction.type === 'adding' ||
      transaction.type === 'selling'
    ) {
      return '-';
    }
    const amount =
      (transaction.type === 'speed'
        ? transaction.totalAmount ?? transaction.total
        : transaction.total) || 0;
    return `₹${Number(amount).toLocaleString()}`;
  };

  const getTypeBadgeClass = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'buy') return 'txn-type txn-type-buy';
    if (t === 'sell') return 'txn-type txn-type-sell';
    if (t === 'speed') return 'txn-type txn-type-speed';
    if (t === 'advance' || t === 'given' || t === 'taken') return 'txn-type txn-type-advance';
    return 'txn-type txn-type-other';
  };

  const renderTransactionActions = (transaction) => (
    <div className="txn-actions">
      <button
        type="button"
        className="txn-icon-btn txn-icon-edit"
        title="Edit"
        onClick={() => handleEditTransaction(transaction)}
      >
        <PencilFill />
      </button>
      <button
        type="button"
        className="txn-icon-btn txn-icon-print"
        title="Print"
        onClick={() => handlePrintTransaction(transaction)}
      >
        <PrinterFill />
      </button>
      <button
        type="button"
        className="txn-icon-btn txn-icon-wa"
        title="WhatsApp"
        onClick={() => handleWhatsAppTransaction(transaction)}
      >
        <Whatsapp />
      </button>
      <button
        type="button"
        className="txn-icon-btn txn-icon-danger"
        title="Delete"
        onClick={() => handleDeleteTransaction(transaction._id)}
      >
        <Trash />
      </button>
      <Dropdown align="end" className="txn-more-dropdown">
        <Dropdown.Toggle
          bsPrefix="txn-more-toggle"
          className="txn-icon-btn txn-icon-more"
          title="More"
        >
          <ThreeDotsVertical />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => handleBuyReceipt(transaction)}>
            Buy Receipt
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleSpeedReceipt(transaction)}>
            Speed Receipt
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const renderTransactionTable = () => {
    if (!filteredTransactions.length) {
      return (
        <div className="txn-panel txn-empty">
          <p className="txn-empty-title">No transactions found</p>
          <p className="txn-empty-text">Try changing filters or create a new transaction.</p>
        </div>
      );
    }

    return (
      <>
        <div className="txn-panel txn-table-panel d-none d-md-block">
          <div className="txn-table-scroll">
            <table className="txn-table">
              <thead>
                <tr>
                  <th>Receipt</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Weight</th>
                  <th>Detail</th>
                  <th className="text-end">Total</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction._id}>
                    <td>
                      <span className="txn-receipt">{transaction.receiptNo}</span>
                    </td>
                    <td>
                      <span className="txn-client">
                        {transaction.clientName || transaction.note || '—'}
                      </span>
                    </td>
                    <td>
                      <span className={getTypeBadgeClass(transaction.type || transaction.advanceType)}>
                        {transaction.type || transaction.advanceType || '—'}
                      </span>
                    </td>
                    <td className="txn-muted">
                      {new Date(transaction.date).toLocaleDateString('en-GB')}
                    </td>
                    <td>{formatPureWeight(transaction.pureWeightCalculated)}</td>
                    <td className="txn-muted">{getTransactionDetailCell(transaction)}</td>
                    <td className="text-end txn-amount">{formatTxnTotal(transaction)}</td>
                    <td>
                      <span className={`txn-status txn-status-${getStatusColor(transaction.status)}`}>
                        {transaction.status || '—'}
                      </span>
                    </td>
                    <td className="text-end">{renderTransactionActions(transaction)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="txn-cards d-md-none">
          {filteredTransactions.map((transaction) => (
            <article key={transaction._id} className="txn-card">
              <header className="txn-card-top">
                <div>
                  <div className="txn-receipt">{transaction.receiptNo}</div>
                  <div className="txn-client">{transaction.clientName || transaction.note || '—'}</div>
                </div>
                <span className={`txn-status txn-status-${getStatusColor(transaction.status)}`}>
                  {transaction.status || '—'}
                </span>
              </header>
              <div className="txn-card-grid">
                <div>
                  <span className="txn-label">Type</span>
                  <span className={getTypeBadgeClass(transaction.type || transaction.advanceType)}>
                    {transaction.type || transaction.advanceType || '—'}
                  </span>
                </div>
                <div>
                  <span className="txn-label">Date</span>
                  <span>{new Date(transaction.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div>
                  <span className="txn-label">Weight</span>
                  <span>{formatPureWeight(transaction.pureWeightCalculated)}</span>
                </div>
                <div>
                  <span className="txn-label">Detail</span>
                  <span>{getTransactionDetailCell(transaction)}</span>
                </div>
              </div>
              <footer className="txn-card-bottom">
                <div className="txn-amount">{formatTxnTotal(transaction)}</div>
                {renderTransactionActions(transaction)}
              </footer>
            </article>
          ))}
        </div>
      </>
    );
  };

  const renderSearchFilters = () => (
    <div className="txn-panel txn-filters">
      <Form>
        <Row className="g-3 align-items-end">
          <Col xs={12} sm={6} lg={3}>
            <Form.Group>
              <Form.Label className="txn-filter-label">Receipt No</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search receipt"
                value={searchParams.receiptNo}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    receiptNo: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Form.Group>
              <Form.Label className="txn-filter-label">Client</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search client"
                value={searchParams.clientName}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    clientName: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} lg={2}>
            <Form.Group>
              <Form.Label className="txn-filter-label">Type</Form.Label>
              <Form.Select
                value={searchParams.transactionType}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    transactionType: e.target.value
                  }))
                }
              >
                <option value="">All types</option>
                {TRANSACTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={6} sm={3} lg={2}>
            <Form.Group>
              <Form.Label className="txn-filter-label">From</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.startDate}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    startDate: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={3} lg={2}>
            <Form.Group>
              <Form.Label className="txn-filter-label">To</Form.Label>
              <Form.Control
                type="date"
                value={searchParams.endDate}
                onChange={(e) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    endDate: e.target.value
                  }))
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </div>
  );

  const renderReceiptEditModal = () => {
    if (!selectedTransaction) return null;

    const unit = selectedTransaction.unit || 'g';
    const isPure = selectedTransaction.articleType === 'pureWeight';
    const isLangdi = selectedTransaction.articleType === 'langdiWeight';
    const rows = selectedTransaction.langdiWeight || [];
    const amountGivenLabel =
      selectedTransaction.type === 'buy' ? 'Amount Given' : 'Amount Received';

    return (
      <Modal show={showEditModal} onHide={handleCloseModal} size="xl" className="receipt-edit-modal">
        <Modal.Header closeButton>
          <Modal.Title>Edit Receipt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="receipt-edit-sheet">
            <h1 className="receipt-edit-title">|| SHREE GANESHAY NAMH ||</h1>
            <div className="receipt-edit-header">
              <img src={logo} className="receipt-edit-logo" alt="Logo" />
              <div className="receipt-edit-date-block">
                <div className="receipt-edit-date-label">Date:</div>
                <div>
                  {selectedTransaction.date
                    ? new Date(selectedTransaction.date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : '-'}
                </div>
                <div className="receipt-edit-date-label">Time:</div>
                <div>
                  {selectedTransaction.date
                    ? new Date(selectedTransaction.date).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    : '-'}
                </div>
                <div className="receipt-edit-date-label">Name:</div>
                <div>Manoj Shinde</div>
                <div className="receipt-edit-date-label">Mobile:</div>
                <div>8551881892</div>
              </div>
            </div>

            <div className="receipt-edit-meta">
              <div>
                <strong>Receipt No:</strong>{' '}
                <span className="receipt-edit-receipt-no">{selectedTransaction.receiptNo || 'N/A'}</span>
                <br />
                <strong>Name:</strong>{' '}
                <span className="receipt-edit-client-name">
                  {selectedTransaction.clientName || 'N/A'}
                </span>
              </div>
              {selectedTransaction.clientName && (
                <div className="receipt-edit-whatsapp mt-2">
                  <strong>WhatsApp / Mobile:</strong>{' '}
                  <input
                    type="tel"
                    className="receipt-edit-input receipt-edit-input-whatsapp"
                    value={editWhatsappNo}
                    onChange={(e) => {
                      setEditWhatsappNo(e.target.value);
                      setEditWhatsappStatus('');
                    }}
                    onBlur={handleEditWhatsappBlur}
                    placeholder="Enter WhatsApp number"
                  />
                  <small className="text-muted ms-2">
                    {editWhatsappStatus === 'saving' && 'saving...'}
                    {editWhatsappStatus === 'saved' && 'saved'}
                    {editWhatsappStatus === 'error' && 'save failed'}
                  </small>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newTransaction = addNewTransactionRow(selectedTransaction);
                  const amountGiven = parseFloat(selectedTransaction.amountGiven) || 0;
                  const withGiven = { ...newTransaction, amountGiven };
                  const { totalAmount, totalAmounts } = calculateTransactionAmounts(withGiven);
                  setSelectedTransaction({
                    ...withGiven,
                    totalAmount,
                    pendingAmount: truncateToWholeNumber(totalAmount - amountGiven),
                    totalAmounts,
                    total: totalAmount
                  });
                }}
              >
                Add Row
              </Button>
            </div>

            <table className="receipt-edit-table">
              <thead>
                <tr>
                  <th>Weight</th>
                  {isLangdi && <th>Touch</th>}
                  <th>Pure Weight</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th style={{ width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((weight, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="number"
                        step="0.001"
                        className="receipt-edit-input"
                        value={displayNumberOrEmpty(weight)}
                        onChange={(e) =>
                          setSelectedTransaction(
                            updateTransactionRow(selectedTransaction, index, 'weight', e.target.value)
                          )
                        }
                      />
                      <span className="receipt-edit-unit">{unit}</span>
                    </td>
                    {isLangdi && (
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="receipt-edit-input receipt-edit-input-touch"
                          value={displayNumberOrEmpty(selectedTransaction.touch?.[index])}
                          onChange={(e) =>
                            setSelectedTransaction(
                              updateTransactionRow(selectedTransaction, index, 'touch', e.target.value)
                            )
                          }
                        />
                        <span className="receipt-edit-unit">%</span>
                      </td>
                    )}
                    <td className="receipt-edit-readonly">
                      {getReceiptRowPureWeight(selectedTransaction, index).toFixed(3)} {unit}
                    </td>
                    <td>
                      <span className="receipt-edit-currency">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className="receipt-edit-input receipt-edit-input-rate"
                        value={displayNumberOrEmpty(selectedTransaction.rate?.[index])}
                        onChange={(e) =>
                          setSelectedTransaction(
                            updateTransactionRow(selectedTransaction, index, 'rate', e.target.value)
                          )
                        }
                      />
                      {isLangdi && <span className="receipt-edit-unit">/10g</span>}
                    </td>
                    <td className="receipt-edit-readonly">
                      ₹{getReceiptRowAmount(selectedTransaction, index).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          const updated = removeTransactionRow(selectedTransaction, index);
                          const amountGiven = parseFloat(selectedTransaction.amountGiven) || 0;
                          const withGiven = { ...updated, amountGiven };
                          const { totalAmount, totalAmounts } = calculateTransactionAmounts(withGiven);
                          setSelectedTransaction({
                            ...withGiven,
                            totalAmount,
                            pendingAmount: truncateToWholeNumber(totalAmount - amountGiven),
                            totalAmounts,
                            total: totalAmount
                          });
                        }}
                        disabled={rows.length <= 1}
                      >
                        ×
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="receipt-edit-totals">
              <div className="receipt-edit-total-row">
                <span>Total Amount:</span>
                <span>
                  ₹
                  {truncateToWholeNumber(
                    selectedTransaction.total || selectedTransaction.totalAmount || 0
                  ).toLocaleString()}
                </span>
              </div>
              <div className="receipt-edit-total-row">
                <span>{amountGivenLabel}:</span>
                <span>
                  <span className="receipt-edit-currency">₹</span>
                  <input
                    type="number"
                    step="1"
                    className="receipt-edit-input receipt-edit-input-total"
                    value={selectedTransaction.amountGiven ?? 0}
                    onChange={(e) => handleAmountGivenChange(e.target.value)}
                  />
                </span>
              </div>
              <div className="receipt-edit-total-row">
                <span>Pending Amount:</span>
                <span>
                  ₹{truncateToWholeNumber(selectedTransaction.pendingAmount || 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="receipt-edit-footer">
              <div className="receipt-edit-footer-row">
                <div>* हमारे यहाँ लंगड़ी एक्सचेंज करके मिलता है</div>
                <div>* RSBL गोल्ड कॉइन मिलता है</div>
              </div>
              <div className="receipt-edit-footer-row">
                <div>* AQUA REFINE 995 और 999 मिलता है</div>
                <div>* गोल्ड और सिल्वर मेल्ट किया जाता है</div>
              </div>
              <div className="receipt-edit-powered">powered by Riddhi Infotech - 7499391121</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTransactionEdit} disabled={!selectedTransaction}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderFormEditModal = () => (
  <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Edit Transaction</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {selectedTransaction && (
        <Form>
          {/* Transaction Basic Information */}
          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Receipt No</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.receiptNo || ''}
                disabled
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Client Name</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.clientName || ''}
                disabled
              />
            </Form.Group>
            <Form.Group as={Col} md={4}>
              <Form.Label>Transaction Type</Form.Label>
              <Form.Control
                type="text"
                value={selectedTransaction.type || selectedTransaction.advanceType || 'N/A'}
                disabled
              />
            </Form.Group>
          </Row>
          {selectedTransaction.clientName && (
            <Row className="mb-3">
              <Form.Group as={Col} md={6}>
                <Form.Label>
                  WhatsApp / Mobile No
                  <small className="text-muted ms-2">
                    {editWhatsappStatus === 'saving' && '(saving...)'}
                    {editWhatsappStatus === 'saved' && '(saved)'}
                    {editWhatsappStatus === 'error' && '(save failed)'}
                  </small>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={editWhatsappNo}
                  onChange={(e) => {
                    setEditWhatsappNo(e.target.value);
                    setEditWhatsappStatus('');
                  }}
                  onBlur={handleEditWhatsappBlur}
                  placeholder="Enter WhatsApp number"
                />
              </Form.Group>
            </Row>
          )}

          {/* Transaction Details Table */}
          <div className="transaction-details-section">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Transaction Details</h5>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => {
                  const newTransaction = addNewTransactionRow(selectedTransaction);
                  setSelectedTransaction(newTransaction);
                }}
              >
                Add Row
              </Button>
            </div>

            {isSpeedTransaction(selectedTransaction) && (
              <Row className="mb-3">
                <Form.Group as={Col} md={4}>
                  <Form.Label>Rate (₹ per 10g)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={displayNumberOrEmpty(selectedTransaction.rate?.[0])}
                    onChange={(e) => {
                      const updatedTransaction = updateTransactionRow(
                        selectedTransaction,
                        0,
                        'rate',
                        e.target.value
                      );
                      setSelectedTransaction(updatedTransaction);
                    }}
                  />
                  <Form.Text className="text-muted">
                    One rate applies to all lines (same as Speed create).
                  </Form.Text>
                </Form.Group>
              </Row>
            )}

            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Sr.</th>
                  <th>Weight (gm)</th>
                  {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                    <th>Touch (%)</th>
                  )}
                  {!isSpeedTransaction(selectedTransaction) && <th>Rate (₹ / 10g)</th>}
                  {selectedTransaction.articleType !== 'pureWeight' && <th>Pure Weight</th>}
                  <th>Amount (₹)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedTransaction.langdiWeight.map((weight, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={
                          weight === '' || weight === null || weight === undefined
                            ? ''
                            : typeof weight === 'number' && Number.isNaN(weight)
                              ? ''
                              : weight
                        }
                        onChange={(e) => {
                          const updatedTransaction = updateTransactionRow(
                            selectedTransaction, 
                            index, 
                            'weight', 
                            e.target.value
                          );
                          setSelectedTransaction(updatedTransaction);
                        }}
                      />
                    </td>
                    {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={displayNumberOrEmpty(selectedTransaction.touch[index])}
                          onChange={(e) => {
                            const updatedTransaction = updateTransactionRow(
                              selectedTransaction, 
                              index, 
                              'touch', 
                              e.target.value
                            );
                            setSelectedTransaction(updatedTransaction);
                          }}
                        />
                      </td>
                    )}
                    {!isSpeedTransaction(selectedTransaction) && (
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={displayNumberOrEmpty(selectedTransaction.rate[index])}
                          onChange={(e) => {
                            const updatedTransaction = updateTransactionRow(
                              selectedTransaction, 
                              index, 
                              'rate', 
                              e.target.value
                            );
                            setSelectedTransaction(updatedTransaction);
                          }}
                        />
                      </td>
                    )}
                    {selectedTransaction.articleType !== 'pureWeight' && (
                      <td>
                        {calculatePureWeight(
                          [weight], 
                          [selectedTransaction.touch[index]]
                        )}
                      </td>
                    )}
                    <td>
                      ₹{selectedTransaction.articleType === 'pureWeight'
                        ? truncateToWholeNumber(
                            precisionTruncate(parseFloat(weight) || 0, 3) *
                              ((parseFloat(selectedTransaction.rate[index]) || 0) / 10)
                          ).toLocaleString()
                        : calculateAmount(
                            weight, 
                            selectedTransaction.touch[index], 
                            isSpeedTransaction(selectedTransaction)
                              ? selectedTransaction.rate?.[0]
                              : selectedTransaction.rate[index]
                          ).toLocaleString()
                      }
                    </td>
                    <td>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          const updatedTransaction = removeTransactionRow(
                            selectedTransaction, 
                            index
                          );
                          setSelectedTransaction(updatedTransaction);
                        }}
                        disabled={selectedTransaction.langdiWeight.length <= 1}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr className="table-light">
                  <td colSpan={2}>
                    <strong>Total</strong>
                  </td>
                  {selectedTransaction.touch && selectedTransaction.touch.length > 0 && (
                    <td>—</td>
                  )}
                  {!isSpeedTransaction(selectedTransaction) && <td>—</td>}
                  {selectedTransaction.articleType !== 'pureWeight' && (
                    <td>
                      <strong>
                        {calculatePureWeight(
                          selectedTransaction.langdiWeight,
                          selectedTransaction.touch
                        )}
                      </strong>
                    </td>
                  )}
                  <td>
                    <strong>
                      ₹
                      {truncateToWholeNumber(
                        (selectedTransaction.totalAmounts || []).reduce(
                          (sum, a) => sum + (parseFloat(a) || 0),
                          0
                        )
                      ).toLocaleString()}
                    </strong>
                  </td>
                  <td>—</td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Financial Details */}
          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>
                {isSpeedTransaction(selectedTransaction)
                  ? 'Total (sum of pure × rate / 10)'
                  : 'Total Amount'}
              </Form.Label>
              <Form.Control
                type="number"
                value={truncateToWholeNumber(selectedTransaction.total || selectedTransaction.totalAmount || 0)}
                disabled
              />
            </Form.Group>
            {!isSpeedTransaction(selectedTransaction) && (
              <Form.Group as={Col} md={4}>
                <Form.Label>Amount Given</Form.Label>
                <Form.Control
                  type="number"
                  value={selectedTransaction.amountGiven || 0}
                  onChange={(e) => {
                    const amountGiven = parseFloat(e.target.value) || 0;
                    const totalAmount = truncateToWholeNumber(
                      selectedTransaction.total || selectedTransaction.totalAmount || 0
                    );
                    const updatedTransaction = {
                      ...selectedTransaction,
                      amountGiven,
                      pendingAmount: truncateToWholeNumber(totalAmount - amountGiven)
                    };
                    setSelectedTransaction(updatedTransaction);
                  }}
                />
              </Form.Group>
            )}
            <Form.Group as={Col} md={4}>
              <Form.Label>
                {isSpeedTransaction(selectedTransaction)
                  ? 'Pending (full value at rate)'
                  : 'Pending Amount'}
              </Form.Label>
              <Form.Control
                type="number"
                value={truncateToWholeNumber(selectedTransaction.pendingAmount || 0)}
                disabled
              />
            </Form.Group>
          </Row>

          {isSpeedTransaction(selectedTransaction) && (
            <Row className="mb-3">
              <Form.Group as={Col} md={4}>
                <Form.Label>Balance pure (g)</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    selectedTransaction.balanceWeight ??
                    selectedTransaction.balancePureWeight ??
                    ''
                  }
                  disabled
                />
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>Amount to pay (₹)</Form.Label>
                <Form.Control
                  type="number"
                  value={truncateToWholeNumber(selectedTransaction.amountToBePaid || 0)}
                  disabled
                />
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>Amount to add (₹)</Form.Label>
                <Form.Control
                  type="number"
                  value={truncateToWholeNumber(selectedTransaction.amountToBeAdded || 0)}
                  disabled
                />
              </Form.Group>
            </Row>
          )}

          <Row className="mb-3">
            <Form.Group as={Col} md={4}>
              <Form.Label>Pure Weight Given</Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                value={selectedTransaction.pureWeightGiven ?? 0}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                  setSelectedTransaction((prev) => {
                    const merged = { ...prev, pureWeightGiven: value };
                    return isSpeedTransaction(merged)
                      ? recalculateSpeedFinancials(merged)
                      : merged;
                  });
                }}
                placeholder="Enter Pure Weight Given"
              />
            </Form.Group>
          </Row>
        </Form>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={handleCloseModal}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={saveTransactionEdit}
        disabled={!selectedTransaction}
      >
        Save Changes
      </Button>
    </Modal.Footer>
  </Modal>
  );

  const renderEditModal = () => {
    if (!showEditModal) return null;
    if (selectedTransaction && shouldUseReceiptEditView(selectedTransaction)) {
      return renderReceiptEditModal();
    }
    return renderFormEditModal();
  };

// Helper Functions for Transaction Editing
const addNewTransactionRow = (transaction) => {
  const lastIndex = transaction.langdiWeight.length - 1;
  const newWeight = transaction.langdiWeight[lastIndex] || 0;
  const baseRate =
    isSpeedTransaction(transaction) && transaction.rate?.length
      ? parseFloat(transaction.rate[0]) || 0
      : parseFloat(transaction.rate?.[lastIndex]) || 0;
  const newTransaction = {
    ...transaction,
    langdiWeight: [
      ...transaction.langdiWeight, 
      newWeight
    ],
    touch: transaction.articleType === 'pureWeight' 
      ? [] 
      : [
          ...transaction.touch, 
          transaction.touch[lastIndex] || 0
        ],
    rate: isSpeedTransaction(transaction)
      ? [...transaction.langdiWeight, newWeight].map(() => baseRate)
      : [
          ...transaction.rate, 
          transaction.rate[lastIndex] || 0
        ],
    totalAmounts: [
      ...transaction.totalAmounts, 
      0
    ]
  };
  if (transaction.articleType === 'langdiWeight' || transaction.articleType === 'pureWeight') {
    newTransaction.amount = [...(transaction.amount || []), newWeight];
  }
  if (isSpeedTransaction(newTransaction)) {
    return recalculateSpeedFinancials(newTransaction);
  }
  return newTransaction;
};

const removeTransactionRow = (transaction, indexToRemove) => {
  const next = {
    ...transaction,
    langdiWeight: transaction.langdiWeight.filter((_, index) => index !== indexToRemove),
    touch: transaction.touch.filter((_, index) => index !== indexToRemove),
    rate: transaction.rate.filter((_, index) => index !== indexToRemove),
    totalAmounts: transaction.totalAmounts.filter((_, index) => index !== indexToRemove)
  };
  if (
    (transaction.articleType === 'langdiWeight' || transaction.articleType === 'pureWeight') &&
    Array.isArray(transaction.amount)
  ) {
    next.amount = transaction.amount.filter((_, index) => index !== indexToRemove);
  }
  if (isSpeedTransaction(next) && next.langdiWeight.length > 0) {
    const base = parseFloat(transaction.rate?.[0]) || 0;
    next.rate = next.langdiWeight.map(() => base);
    return recalculateSpeedFinancials(next);
  }
  return next;
};

  return (
    <div className="txn-page">
      <div className="txn-header">
        <div>
          <h1 className="txn-title">All Transactions</h1>
          <p className="txn-subtitle">
            {filteredTransactions.length} shown
            {transactions.length !== filteredTransactions.length
              ? ` of ${transactions.length}`
              : ''}
          </p>
        </div>
      </div>

      {error && (
        <div className="txn-alert txn-alert-error" role="alert">
          <span>{error}</span>
          <button type="button" className="txn-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {renderSearchFilters()}
      {renderTransactionTable()}
      {renderEditModal()}
    </div>
  );
};

export default Transactions;

// Utility function for status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'success';
    case 'pending': return 'warning';
    case 'cancelled': return 'danger';
    default: return 'secondary';
  }
};
